import React, { FC, useRef, useState, useEffect } from "react";
import styles from "./styles.module.less";
import { Spin, Button } from "antd";
import { useTranslation } from "react-i18next";
import { AppInfo } from "../types";
import {
  SortableElement,
  SortableContainer,
  arrayMove,
  SortableContainerProps,
} from "react-sortable-hoc";
import { useTitle } from "@/hooks/useTitle";
import { MobileAppItemCard } from "../MobileAppItemCard";
import classNames from "classnames";
import { onTap } from "@/utils/mobileEvent";

export const MobileAppList: FC<{
  orderedApps: AppInfo[];
  allApps: AppInfo[];
  setOrderedApps: React.Dispatch<React.SetStateAction<AppInfo[]>>;
  loadingOrderedApp: boolean;
  removeAppRequest: (id: string | string[]) => Promise<void>;
  fetchOrderedApps: () => Promise<void>;
  sortApps: (ids: string[]) => Promise<void>;
  onShowAddApp: () => void;
}> = ({
  loadingOrderedApp,
  orderedApps,
  allApps,
  setOrderedApps,
  removeAppRequest,
  sortApps,
  onShowAddApp,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  useTitle(`${window.__config__.name} ${t("common.appPanel")}`, "");

  const [submitting, setSubmitting] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const removeListRef = useRef<string[]>([]);

  useEffect(() => {
    containerRef.current?.addEventListener("contextmenu", (e) =>
      e.preventDefault()
    );
  }, []);

  const AddButton = () => {
    return (
      <div className={styles.addBtnContainer}>
        <div
          className={styles.addBtn}
          onTouchStart={(evt) => {
            onTap(evt.target, onShowAddApp);
          }}
        >
          <div className={styles.addAppIcon}>+</div>
        </div>
        <p className={styles.addAppText}>{t("common.addApp")}</p>
      </div>
    );
  };

  const SortableItem = SortableElement(({ appInfo }: { appInfo: AppInfo }) => (
    <MobileAppItemCard
      appInfo={appInfo}
      showDeleteIcon
      onRemove={() => {
        removeListRef.current.push(appInfo.id);
        setOrderedApps((apps) => apps.filter((item) => item.id !== appInfo.id));
      }}
    />
  ));
  const SortableAppContainer: React.ComponentClass<SortableContainerProps, FC> =
    SortableContainer(({ children }: any) => (
      <div className={styles.sortContainer}>{children}</div>
    ));

  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.editing]: editMode,
        })}
      >
        <Spin spinning={loadingOrderedApp || submitting}>
          <div ref={containerRef} className={styles.containerWrap}>
            <div className={styles.appList}>
              {editMode && (
                <SortableAppContainer
                  onSortEnd={async ({ oldIndex, newIndex }: any) => {
                    if (oldIndex === newIndex) {
                      return;
                    }

                    const nextOrders = arrayMove(
                      orderedApps,
                      oldIndex,
                      newIndex
                    );
                    setOrderedApps(nextOrders);
                  }}
                  axis="xy"
                  // pressDelay={1000}
                >
                  {Boolean(allApps?.length) && !loadingOrderedApp && (
                    <AddButton />
                  )}

                  {Boolean(orderedApps?.length) &&
                    orderedApps.map((v, index) => {
                      return (
                        <SortableItem
                          key={`item-${v.id}`}
                          index={index}
                          appInfo={v}
                          disabled={!editMode}
                        />
                      );
                    })}
                </SortableAppContainer>
              )}

              {!editMode && (
                <div className={styles.sortContainer}>
                  {Boolean(allApps?.length) && !loadingOrderedApp && (
                    <AddButton />
                  )}

                  {Boolean(orderedApps?.length) &&
                    orderedApps.map((v) => {
                      return (
                        <MobileAppItemCard
                          key={v.id}
                          appInfo={v}
                          onLongTouch={() => setEditMode(true)}
                        />
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </Spin>
      </div>

      {editMode && (
        <Button
          loading={submitting}
          onClick={async () => {
            setSubmitting(true);
            try {
              await removeAppRequest(removeListRef.current);
              await sortApps(orderedApps.map((item) => item.id));
              setEditMode(false);
            } catch (e) {
              console.error(e);
            } finally {
              setSubmitting(false);
            }
          }}
          className={styles.editFinishBtn}
          block
        >
          {t("common.finish")}
        </Button>
      )}
    </>
  );
};
